/**
 * <attached-file> 标签的统一编解码工具。
 *
 * 模型侧约定：用户上传文档时，前端把文档原文用 <attached-file name="..." type="..."> 块
 * 包起来注入到 user message 的 text part。模型据此识别附件边界。
 *
 * 注意：文档原文可能包含字面量 `</attached-file>` / `<` 等字符，会破坏正则匹配，
 * 因此注入前对原文整体做 XML escape，渲染回正文时反向 unescape。
 */

export interface AttachedFileMeta {
  name: string
  mimeType: string
  size: number
}

export function escapeAttachedFileAttr(s: string): string {
  return s.replace(/&/g, '&amp;').replace(/"/g, '&quot;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
}

export function unescapeAttachedFileAttr(s: string): string {
  return s.replace(/&quot;/g, '"').replace(/&lt;/g, '<').replace(/&gt;/g, '>').replace(/&amp;/g, '&')
}

/**
 * 把文档原文用 <attached-file> 包起来。原文整体 XML 转义，避免字面量
 * `</attached-file>` 截断块，渲染端再反转义即可还原。
 */
export function buildAttachedFileBlock(meta: AttachedFileMeta, text: string): string {
  return `<attached-file name="${escapeAttachedFileAttr(meta.name)}" type="${escapeAttachedFileAttr(meta.mimeType)}">\n${escapeAttachedFileAttr(text)}\n</attached-file>`
}

/**
 * 创建一个全新的全局正则。每次调用都返回新实例，避免共享 lastIndex 在
 * 并发 computed 间踩坑。
 *
 * 属性顺序写死为 name 后 type，与 buildAttachedFileBlock 输出对齐。
 */
function createAttachedFileRegExp(): RegExp {
  return /<attached-file\s+name="([^"]*)"\s+type="([^"]*)">([\s\S]*?)<\/attached-file>/g
}

/**
 * 把 text 中的 <attached-file> 块全部移除，返回干净正文，用于显示。
 */
export function stripAttachedFiles(text: string): string {
  return text.replace(createAttachedFileRegExp(), '').replace(/\n{3,}/g, '\n\n')
}

/**
 * 从 text 中提取所有 <attached-file> 块的元信息（name / mimeType / size）。
 * size 用 escape 后的字符长度近似，仅用于 UI 显示，不参与业务判断。
 *
 * 仅在结构化 attachments 字段缺失时作为旧消息兜底用。
 */
export function extractAttachedFiles(text: string): AttachedFileMeta[] {
  if (!text)
    return []
  const re = createAttachedFileRegExp()
  const out: AttachedFileMeta[] = []
  let m: RegExpExecArray | null = re.exec(text)
  while (m !== null) {
    out.push({
      name: unescapeAttachedFileAttr(m[1]),
      mimeType: unescapeAttachedFileAttr(m[2]),
      size: m[3].length,
    })
    m = re.exec(text)
  }
  return out
}
